import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { cn } from '@/components/ui/cn'
import { UNMASK_AREA } from '@/lib/analytics'
import { tabLabel, visibleTabs, type Community } from '@/lib/community'
import { usePlatformStatus } from '@/lib/platform'
import { revealInScroller } from '@/lib/scroll'
import { t } from '@/lib/i18n'
import { activeTabFor } from './shell-utils'

type Fade = 'none' | 'left' | 'right' | 'both'

/** True unless the platform says map tiles are off (REDESIGN section 7); unknown or missing reads as on. */
function useMapTilesEnabled(): boolean {
  const { features } = usePlatformStatus()
  return (features as { mapTiles?: unknown }).mapTiles !== false
}

/** Skool style tab row (h44, not sticky): 15px, muted inactive, active text 600 with a 3px dark underline. */
export function CommunityTabs({ slug, community, pathname }: { slug: string; community: Community | null; pathname: string }) {
  const mapTiles = useMapTilesEnabled()
  const tabs = visibleTabs(community).filter((tab) => mapTiles || tab.key !== 'map')
  const active = activeTabFor(pathname, slug)
  const scroller = useRef<HTMLDivElement>(null)
  const [fade, setFade] = useState<Fade>('none')

  const updateFade = () => {
    const el = scroller.current
    if (!el) return
    const left = el.scrollLeft > 2
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
    setFade(left && right ? 'both' : left ? 'left' : right ? 'right' : 'none')
  }

  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const center = () => {
      const activeEl = el.querySelector<HTMLElement>('[aria-current="page"]')
      if (activeEl) revealInScroller(el, activeEl, { center: true })
      updateFade()
    }
    center()
    if (typeof ResizeObserver === 'undefined') return
    // Tabs get wider once the web font loads: centre again, or the last tab stays clipped (Q-COMM-32).
    const ro = new ResizeObserver(center)
    ro.observe(el)
    el.querySelectorAll('a').forEach((a) => ro.observe(a))
    return () => ro.disconnect()
  }, [active, tabs.length])

  return (
    <div className="border-b border-line bg-surface">
      <div className="mx-auto w-full max-w-[1080px] px-4 lg:px-6">
        <div
          ref={scroller}
          onScroll={updateFade}
          // A tab reached with Tab scrolls clear of the fade masks (Q-SRCH-31).
          onFocus={(e) => {
            const el = scroller.current
            if (el && e.target instanceof HTMLElement) revealInScroller(el, e.target)
          }}
          className={cn(
            'scrollbar-none -mx-4 scroll-px-8 overflow-x-auto px-4 md:mx-0 md:px-0',
            fade === 'both' && 'fade-x',
            fade === 'right' && 'fade-r',
            fade === 'left' && 'fade-l',
          )}
        >
          <nav aria-label={t('Community sections')} className="flex h-11 w-max min-w-full items-stretch" {...UNMASK_AREA}>
            {tabs.map((tab, i) => {
              const isActive = tab.key === active
              return (
                <Link
                  key={tab.key}
                  to={`/${community?.slug ?? slug}${tab.path}`}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'relative flex shrink-0 items-center whitespace-nowrap text-tab outline-none transition-colors duration-150',
                    'focus-visible:rounded-sm focus-visible:shadow-[inset_0_0_0_2px_var(--color-focus-ring)]',
                    i === 0 ? 'pr-3.5' : 'px-3.5',
                    isActive ? 'font-semibold text-text' : 'text-subtle hover:text-text',
                  )}
                >
                  {tabLabel(tab.key)}
                  {isActive ? (
                    <span
                      aria-hidden="true"
                      className={cn('absolute bottom-0 h-[3px] rounded-t-[3px] bg-text', i === 0 ? 'left-0 right-3.5' : 'inset-x-3.5')}
                    />
                  ) : null}
                </Link>
              )
            })}
          </nav>
        </div>
      </div>
    </div>
  )
}
